import { useNavigate } from "react-router-dom";

const warehouses = [
  {
    id: 1,
    region: "USA",
    flag: "🇺🇸",
    city: "Los Angeles, CA",
    area: "50,000 sqft",
    delivery: "1–3 days",
    skus: "12,000+",
    color: "from-red-50 to-rose-50",
    accent: "text-red-500",
    border: "border-red-100",
    image: "https://public.readdy.ai/ai/img_res/7c25c6fd030a1a318bf0c2f112516532.jpg",
  },
  {
    id: 2,
    region: "Canada",
    flag: "🇨🇦",
    city: "Toronto, ON",
    area: "40,000 sqft",
    delivery: "2–4 days",
    skus: "8,900+",
    color: "from-red-50 to-orange-50",
    accent: "text-orange-500",
    border: "border-orange-100",
    image: "https://readdy.ai/api/search-image?query=modern%20warehouse%20interior%20Canada%20Toronto%20logistics%20distribution%20center%20shelves%20packages%20professional%20photography%20wide%20angle%20industrial&width=600&height=380&seq=wh_canada&orientation=landscape",
  },
  {
    id: 3,
    region: "Europe",
    flag: "🇪🇺",
    city: "Rotterdam, Netherlands",
    area: "80,000 sqft",
    delivery: "2–5 days",
    skus: "18,000+",
    color: "from-amber-50 to-yellow-50",
    accent: "text-amber-500",
    border: "border-amber-100",
    image: "https://readdy.ai/api/search-image?query=modern%20warehouse%20interior%20Europe%20Rotterdam%20Netherlands%20logistics%20distribution%20center%20shelves%20packages%20professional%20photography%20wide%20angle%20industrial&width=600&height=380&seq=wh_europe&orientation=landscape",
  },
  {
    id: 4,
    region: "Latin America",
    flag: "🌎",
    city: "São Paulo, Brazil",
    area: "35,000 sqft",
    delivery: "3–6 days",
    skus: "6,500+",
    color: "from-green-50 to-emerald-50",
    accent: "text-green-600",
    border: "border-green-100",
    image: "https://readdy.ai/api/search-image?query=modern%20warehouse%20interior%20Latin%20America%20Brazil%20Sao%20Paulo%20logistics%20distribution%20center%20shelves%20packages%20professional%20photography%20wide%20angle%20industrial&width=600&height=380&seq=wh_latam&orientation=landscape",
  },
];

const features = [
  { icon: "ri-store-2-line", title: "Sellers Upload Products", desc: "Sign up as a seller, upload product photos, descriptions and set commission rates for influencers." },
  { icon: "ri-megaphone-line", title: "Influencers Promote & Earn", desc: "Facebook, TikTok, Twitter & YouTube stars promote products and earn commission on every sale." },
  { icon: "ri-shopping-cart-line", title: "Buyers Place Orders", desc: "Buyers sign up, browse verified dropship products and place orders with fast local delivery." },
  { icon: "ri-truck-line", title: "Fast Local Delivery", desc: "We ship from the nearest regional warehouse — USA, Canada, Europe or Latin America." },
];

export default function WarehouseSection() {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Global Fulfillment</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
              Overseas Warehouse<br className="hidden md:block" /> Drop Shipping
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl">
              Sell globally without holding inventory. Sellers upload products, influencers promote, buyers order — we ship from the nearest warehouse in 1–6 days.
            </p>
          </div>
          <button
            onClick={() => navigate("/overseas-warehouse")}
            className="hidden md:flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Start Dropshipping <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Warehouse cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {warehouses.map((w) => (
            <div
              key={w.id}
              onClick={() => navigate("/overseas-warehouse")}
              className={`bg-gradient-to-br ${w.color} border ${w.border} rounded-2xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300`}
            >
              <div className="w-full h-40 overflow-hidden">
                <img
                  src={w.image}
                  alt={w.region}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{w.flag}</span>
                  <div>
                    <h3 className="font-extrabold text-gray-900 text-base">{w.region}</h3>
                    <p className="text-xs text-gray-500">{w.city}</p>
                  </div>
                  <span className="ml-auto bg-green-100 text-green-600 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">Active</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white/70 rounded-lg p-2">
                    <div className={`text-xs font-bold ${w.accent}`}>{w.delivery}</div>
                    <div className="text-xs text-gray-400">Delivery</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2">
                    <div className="text-xs font-bold text-gray-800">{w.skus}</div>
                    <div className="text-xs text-gray-400">SKUs</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2">
                    <div className="text-xs font-bold text-gray-800">{w.area}</div>
                    <div className="text-xs text-gray-400">Area</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl p-8 mb-8">
          <h3 className="text-xl font-extrabold text-gray-900 text-center mb-8">How It Works</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={f.title} className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-2xl">
                    <i className={`${f.icon} text-amber-500 text-2xl`}></i>
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-amber-400 text-white text-xs font-bold rounded-full">{i + 1}</span>
                </div>
                <h4 className="font-bold text-gray-800 text-sm mb-1">{f.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate("/overseas-warehouse")}
            className="flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-2-line"></i> Sign Up as Seller / Supplier
          </button>
          <button
            onClick={() => navigate("/overseas-warehouse")}
            className="flex items-center justify-center gap-2 bg-white border-2 border-amber-400 text-amber-500 font-bold px-8 py-3.5 rounded-full hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line"></i> Shop as Buyer
          </button>
          <button
            onClick={() => navigate("/overseas-warehouse")}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 font-bold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-megaphone-line"></i> Join as Influencer
          </button>
        </div>
      </div>
    </section>
  );
}
